'use client';

import React, { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { motion, useAnimation, Variants } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import FadeLoader from 'react-spinners/FadeLoader';
import toast, { Toaster } from 'react-hot-toast';
import { FBPIXPush } from '@/services/fbConversionService';
import { AK, APPENUMS } from '@/constants';
import { AxiosPost_Nocatch } from '@/utilities/axioscall';

// Mock order status list (replace with your actual data)
const orderstatusListData = [
  { id: '0', status: 'Pending' },
  { id: '1', status: 'Success' },
  { id: '3', status: 'Shipped' },
];

interface FormValues {
  trackorderID: string;
  trackordercreatedAt: string;
  trackorderStatus: string;
  trackorderTrackingNo: string;
  trackorderTrackingCourier: string;
  trackorderTrackingLink: string;
  email: string;
  phone: string;
  about: string;
  trackorderIDError: boolean;
  emailError: boolean;
  phoneError: boolean;
  aboutError: boolean;
  formloading: boolean;
}

export default function TrackOrder() {
  const [pageinit, setPageInit] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    trackorderID: '',
    trackordercreatedAt: '',
    trackorderStatus: '',
    trackorderTrackingNo: '',
    trackorderTrackingCourier: '',
    trackorderTrackingLink: '',
    email: '',
    phone: '',
    about: '',
    trackorderIDError: false,
    emailError: false,
    phoneError: false,
    aboutError: false,
    formloading: false,
  });

  const controls = useAnimation();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    if (!pageinit) {
      setPageInit(true);
      FBPIXPush(APPENUMS.EVENT_ENUMS.TRACK_ORDER);
    }
  }, [pageinit]);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants: Variants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
        staggerChildren: 0.2,
      },
    },
  };

  const childVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const notify = () =>
    toast.success('Track Order fetched successfully.', {
      style: {
        border: '1px solid #00b852',
        padding: '16px',
        color: '#00b852',
      },
      iconTheme: {
        primary: '#00b852',
        secondary: '#FFFAEE',
      },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: {
        border: '1px solid #cc0000',
        padding: '16px',
        color: '#cc0000',
      },
      iconTheme: {
        primary: '#cc0000',
        secondary: '#FFFAEE',
      },
    });

  const handleChange = (e: { target: { name: string; value: string | boolean } }) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let error = false;

    if (formValues.trackorderID === '') {
      handleChange({
        target: {
          value: true,
          name: 'trackorderIDError',
        },
      });
      error = true;
    } else {
      handleChange({
        target: {
          value: false,
          name: 'trackorderIDError',
        },
      });
    }

    if (error) return;

    handleChange({
      target: {
        value: true,
        name: 'formloading',
      },
    });

    const payload = {
      orderid: formValues.trackorderID,
    };

    try {
      const res = await AxiosPost_Nocatch(AK.TRACKORDERAPI, payload);
      console.log('API Response:', res); // Debug the response
      if (res && res.data && res.data.data) {
        const {
          order_id,
          created_at,
          order_status,
          tracking_awb,
          tracking_courier,
          tracking_link,
        } = res.data.data;
        notify();
        setFormValues({
          trackorderID: order_id || '',
          trackordercreatedAt: created_at || '',
          trackorderStatus: order_status?.toString() || '',
          trackorderTrackingNo: tracking_awb || '',
          trackorderTrackingCourier: tracking_courier || '',
          trackorderTrackingLink: tracking_link || '',
          email: order_id || '',
          phone: '',
          about: '',
          trackorderIDError: false,
          emailError: false,
          phoneError: false,
          aboutError: false,
          formloading: false,
        });
      } else {
        notifyerror('Order ID is invalid.');
        setFormValues((prev) => ({
          ...prev,
          trackorderID: '',
          trackordercreatedAt: '',
          trackorderStatus: '',
          trackorderTrackingNo: '',
          trackorderTrackingCourier: '',
          trackorderTrackingLink: '',
          email: '',
          phone: '',
          about: '',
          formloading: false,
        }));
      }
    } catch (error:any) {
  console.log("error", error);

  let errorMessage = 'Track order failed.';

  if (error.response && error.response.data) {
    if (error.response.data.message) {
      errorMessage = error.response.data.message;

      if (error.response.data.errors && typeof error.response.data.errors === 'object') {
        const validationErrors = Object.values(error.response.data.errors)
          .flat() // Flatten in case of arrays
          .join(', '); // Join multiple errors with commas
        errorMessage += `: ${validationErrors}`;
      }
    }
  }

  // Show the dynamic error message
  notifyerror(errorMessage);

  // Update form state
  setFormValues((prev) => ({
    ...prev,
    formloading: false,
  }));
}
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case '0':
        return 'bg-yellow-200 text-yellow-800';
      case '1':
        return 'bg-green-200 text-green-800';
      case '3':
        return 'bg-blue-200 text-blue-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      className="bg-gradient-to-b from-gray-50 to-white py-12"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1
          variants={childVariants}
          className="text-4xl md:text-5xl font-extrabold text-center text-gray-900 mb-6"
        >
          Track Your Order
        </motion.h1>
        <motion.hr
          variants={childVariants}
          className="w-24 mx-auto border-t-4 border-primary mb-8"
        />
        <motion.div
          variants={childVariants}
          className="max-w-md mx-auto"
        >
          <p className="text-center text-gray-600 mb-6">
            Enter your Order ID to track your package. For assistance,{' '}
            <a href="/contactus" className="text-primary hover:underline">
              contact us
            </a>.
          </p>
          <form onSubmit={handleSubmit}>
            <motion.div variants={childVariants} className="mb-4">
              <label
                htmlFor="trackorderID"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Order ID *
              </label>
              <input
                type="text"
                name="trackorderID"
                id="trackorderID"
                className="w-full rounded-md border-2 border-solid p-[10px] !border-gray-300 shadow-sm focus:!border-primary focus:!ring-primary sm:text-sm"
                value={formValues.trackorderID}
                onChange={handleChange}
                placeholder="Enter your order ID"
              />
              {formValues.trackorderIDError && (
                <p className="mt-1 text-sm text-red-500">Please enter your order ID.</p>
              )}
            </motion.div>
            <motion.div
              variants={childVariants}
              className="flex justify-center items-center gap-4"
            >
              <button
                type="submit"
                className="rounded-md !bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center gap-2"
              >
                Track
                <Truck className="w-5 h-5" />
              </button>
              <FadeLoader
                loading={formValues.formloading}
                width={2}
                height={10}
                color="rgb(98 154 40)"
              />
            </motion.div>
          </form>
        </motion.div>
        {formValues.email && (<>
         <div className="mt-8 shadow-md max-w-4xl mx-auto overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
  <table className="w-full border-collapse bg-white shadow-sm rounded">
    <thead className="rounded-t-md overflow-hidden">
      <tr className="bg-gray-200 text-gray-800 rounded-t-md overflow-hidden">
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Order ID</th>
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Created At</th>
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Order Status</th>
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Tracking No</th>
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Tracking Courier</th>
        <th className="border border-gray-400/10 p-3 text-left whitespace-nowrap font-serif font-medium">Tracking Link</th>
      </tr>
    </thead>
    <tbody className="rounded-b-md">
      <tr className="bg-gray-100 rounded-b-md">
        <td className="border border-gray-400/10 p-3 font-serif">{formValues.email}</td>
        <td className="border whitespace-nowrap border-gray-400/10 p-3 font-serif">
          {new Date(formValues.trackordercreatedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'Asia/Kolkata',
          })}
        </td>
        <td className={`border border-gray-400/10 p-3 font-serif ${getStatusStyle(formValues.trackorderStatus)}`}>
          {orderstatusListData.find((item) => item.id === formValues.trackorderStatus)?.status || 'Unknown'}
        </td>
        <td className="border border-gray-400/10 p-3 font-serif">{formValues.trackorderTrackingNo}</td>
        <td className="border border-gray-400/10 p-3 font-serif">{formValues.trackorderTrackingCourier}</td>
        <td className="border border-gray-400/10 p-3 font-serif">
          {formValues.trackorderTrackingLink !== '-' ? (
            <a
              href={formValues.trackorderTrackingLink}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Track
            </a>
          ) : (
            'N/A'
          )}
        </td>
      </tr>
    </tbody>
  </table>
</div>
<style>
{`
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #e5e7eb;
  }
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
  }
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 4px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`}
</style>
      </>  )}
      </div>
      <Toaster />
    </motion.section>
  );
}
